// Preview projection — a read-only, client-side layout of a cookpit file's
// phases for the `/preview` walkthrough (README §6.4 Preview).
//
// Preview is NOT the cook. It makes no /anchor call and runs no live clock;
// it lets the user read every step, follow chef-hat links and open help
// before committing. Times are calculated ONCE, at the moment Preview is
// displayed:
//   - if the Planner has a serve time, anchor to it (so the Preview times
//     match the real cook exactly, via the same computeWholeCookPlan);
//   - if not, anchor so the cook starts now.
// Either way the per-step offsets come from the same parseTaskTimeSeconds
// the server projection uses, so Preview and the cook can never drift.
//
// A file with no timed prep phase but prerequisites mise shows a DERIVED
// PrepCook phase: untimed checklist rows under a phase window header
// (DECISIONS §E.7). The derived phase occupies its budget (declared
// prepTime, else the 30-minute advisory default) before LiveCook.

import {
  computeWholeCookPlan,
  parseTaskTimeSeconds,
  parsePhaseDurationSeconds,
  type Cookpit,
  type PhaseId,
  type ServeTime,
} from '@cookchow/shared';
import { formatHHMM } from './helpers';
import { buildNoteLookup, notesForRefs, type NoteEntry } from './notes';
import {
  buildPrepChecklist,
  computePrepBudget,
  derivedPrepLeadSeconds,
  shouldDerivePrepChecklist,
} from './derivePrep';

type CookpitFile = Cookpit.CookpitCookingFileV32;
type Recipe = Readonly<CookpitFile>;

export interface PreviewStep {
  id: string;
  action: string;
  /** Lane suffix (A0, S1…) for the lane chip. Absent on derived rows. */
  lane?: string;
  /** HH:MM wall-clock start/end. Omitted on untimed derived rows. */
  startLabel?: string;
  endLabel?: string;
  /** Resolved chef-hat help (skills / notes / hotspots). */
  help: NoteEntry[];
  /** One-line resources/tools summary for derived prep rows. */
  meta?: string;
}

export interface PreviewPhaseData {
  phase: PhaseId;
  /** Display label: 'PreCook' | 'PrepCook' | 'LiveCook'. */
  label: string;
  /** True for the prerequisites-derived PrepCook checklist (untimed rows). */
  derived: boolean;
  /** HH:MM phase window for the column header. */
  windowStart: string;
  windowEnd: string;
  steps: PreviewStep[];
}

/** One Preview screen, navigated to by a chip. The combined Pre/PrepCook
 *  screen carries both prep phases; every other screen carries one. */
export interface PreviewScreen {
  id: string;
  /** Chip label: 'PreCook' | 'PrepCook' | 'Pre/PrepCook' | 'LiveCook'. */
  chipLabel: string;
  phases: PreviewPhaseData[];
}

const PHASE_LABEL: Record<PhaseId, string> = {
  preCook: 'PreCook',
  prepCook: 'PrepCook',
  liveCook: 'LiveCook',
};

// Build the resources/tools one-liner for a derived prep row, mirroring the
// PrepChecklistPane's StepMeta so Preview and the cook read alike.
function metaFor(step: {
  resources: { text: string; measure?: string }[];
  utensils: string[];
  sundries: string[];
}): string | undefined {
  const parts: string[] = [];
  for (const r of step.resources) parts.push(r.measure ? `${r.measure} ${r.text}` : r.text);
  const tools = [...step.utensils, ...step.sundries];
  if (tools.length > 0) parts.push(`— ${tools.join(', ')}`);
  return parts.length > 0 ? parts.join('  ·  ') : undefined;
}

type RawTask = { id: string; time: string; action: string; kind?: string };

// The cookpit "Start" cue — the earliest lane-A0 alarm in a phase (action
// "Start."). CookChow omits it from Preview, mirroring the live cook
// (see phaseStartCue.ts). Other A0 alarms ("Ten minutes remaining", "Time
// up") are kept.
function startCueId(tasks: readonly RawTask[]): string | null {
  let id: string | null = null;
  let best = Number.POSITIVE_INFINITY;
  for (const t of tasks) {
    if (t.kind !== 'alarm') continue;
    const { seconds, lane } = parseTaskTimeSeconds(t.time);
    if (lane !== 'A0') continue;
    if (seconds < best) {
      best = seconds;
      id = t.id;
    }
  }
  return id;
}

// Project one timed phase to ordered, time-labelled steps. `phaseStartMs`
// is the phase's wall-clock origin (relative offset 0); each task lands at
// origin + its authored offset, and ends at the next task's start (or the
// phase end for the last). Same arithmetic as the server projection.
function projectTimedPhase(
  phase: { nominalDuration: string; tasks: RawTask[] },
  phaseId: PhaseId,
  phaseStartMs: number,
  lookup: Map<string, NoteEntry>,
  chefHatRefsFor: (taskId: string) => string[],
): PreviewPhaseData {
  const nominalSec = parsePhaseDurationSeconds(phase.nominalDuration);
  const phaseEndMs = phaseStartMs + nominalSec * 1000;
  const cueId = startCueId(phase.tasks);
  const ordered = phase.tasks
    .filter((t) => t.id !== cueId)
    .map((t) => {
      const { seconds, lane } = parseTaskTimeSeconds(t.time);
      return { task: t, offset: seconds, lane, startMs: phaseStartMs + seconds * 1000 };
    })
    .sort((a, b) => a.offset - b.offset);

  const steps: PreviewStep[] = ordered.map((o, i) => {
    const endMs = i + 1 < ordered.length ? ordered[i + 1]!.startMs : phaseEndMs;
    return {
      id: o.task.id,
      action: o.task.action,
      lane: o.lane,
      startLabel: formatHHMM(new Date(o.startMs).toISOString()),
      endLabel: formatHHMM(new Date(endMs).toISOString()),
      help: notesForRefs(chefHatRefsFor(o.task.id), lookup),
    };
  });

  return {
    phase: phaseId,
    label: PHASE_LABEL[phaseId],
    derived: false,
    windowStart: formatHHMM(new Date(phaseStartMs).toISOString()),
    windowEnd: formatHHMM(new Date(phaseEndMs).toISOString()),
    steps,
  };
}

// Resolve the wall-clock serve instant the preview anchors to: the Planner's
// serve time when set, else a synthetic serve placed so the earliest activity
// lands on `nowMs` ("starts now"). The probe gives totalSeconds (= max(prep)
// + liveCook), independent of the serve instant; the derived-prep lead is
// added so a checklist-only file still starts its prep at now.
function resolveServeMs(recipe: Recipe, serveTime: ServeTime | null, nowMs: number): number {
  if (serveTime) return Date.parse(serveTime.instant);
  const lead = derivedPrepLeadSeconds(recipe);
  return nowMs + (computeWholeCookPlan(recipe.cookpit, nowMs).totalSeconds + lead) * 1000;
}

/**
 * Build the Preview phases for a recipe, anchored to `serveTime` when set or
 * to `nowMs` otherwise. Returns present phases in order (preCook, prepCook
 * [or derived], liveCook).
 */
export function buildPreviewPhases(
  recipe: Recipe,
  serveTime: ServeTime | null,
  nowMs: number,
): PreviewPhaseData[] {
  const cp = recipe.cookpit;
  const lookup = buildNoteLookup(recipe);
  const prereqs = cp.prerequisites;
  const chefHatRefsFor = (taskId: string): string[] => {
    const acc = new Set<string>();
    for (const bucket of [prereqs?.hotspots, prereqs?.skills, prereqs?.notes]) {
      for (const item of bucket ?? []) {
        const refs = (item as { taskRefs?: string[] }).taskRefs;
        if (refs?.includes(taskId)) acc.add(item.id);
      }
    }
    return [...acc].sort();
  };

  // The derived checklist (when present) occupies this lead before LiveCook.
  const lead = derivedPrepLeadSeconds(recipe);

  const serveMs = resolveServeMs(recipe, serveTime, nowMs);

  const wc = computeWholeCookPlan(cp, serveMs);
  const startByPhase = new Map(wc.phases.map((p) => [p.phase, p.startMs]));

  const out: PreviewPhaseData[] = [];

  if (cp.preCook) {
    out.push(
      projectTimedPhase(
        cp.preCook,
        'preCook',
        startByPhase.get('preCook')!,
        lookup,
        chefHatRefsFor,
      ),
    );
  }
  if (cp.prepCook) {
    out.push(
      projectTimedPhase(
        cp.prepCook,
        'prepCook',
        startByPhase.get('prepCook')!,
        lookup,
        chefHatRefsFor,
      ),
    );
  }

  // Derived PrepCook: untimed rows inside the [liveStart − lead, liveStart]
  // window. Mutually exclusive with timed prep, so it only fires here.
  if (shouldDerivePrepChecklist(recipe)) {
    const windowEndMs = wc.liveStartMs;
    const windowStartMs = windowEndMs - lead * 1000;
    const budget = computePrepBudget(recipe);
    const checklist = buildPrepChecklist(recipe);
    out.push({
      phase: 'prepCook',
      label: PHASE_LABEL.prepCook,
      derived: true,
      windowStart: formatHHMM(new Date(windowStartMs).toISOString()),
      windowEnd: formatHHMM(new Date(windowEndMs).toISOString()),
      steps: checklist.map((s) => ({
        id: s.id,
        action: s.text,
        help: s.help,
        meta: metaFor(s),
      })),
    });
    void budget; // budget seconds inform the window above; no separate chip
  }

  out.push(
    projectTimedPhase(
      cp.liveCook,
      'liveCook',
      startByPhase.get('liveCook')!,
      lookup,
      chefHatRefsFor,
    ),
  );

  return out;
}

/** Group phases into chip-navigable screens. Both timed prep phases share
 *  one combined "Pre/PrepCook" screen; otherwise each phase is its own. */
export function groupPreviewScreens(phases: PreviewPhaseData[]): PreviewScreen[] {
  const preCook = phases.find((p) => p.phase === 'preCook' && !p.derived);
  const prepCook = phases.find((p) => p.phase === 'prepCook');
  const liveCook = phases.find((p) => p.phase === 'liveCook')!;

  const screens: PreviewScreen[] = [];
  if (preCook && prepCook) {
    screens.push({ id: 'prep', chipLabel: 'Pre/PrepCook', phases: [preCook, prepCook] });
  } else if (preCook) {
    screens.push({ id: 'preCook', chipLabel: 'PreCook', phases: [preCook] });
  } else if (prepCook) {
    screens.push({ id: 'prepCook', chipLabel: 'PrepCook', phases: [prepCook] });
  }
  screens.push({ id: 'liveCook', chipLabel: 'LiveCook', phases: [liveCook] });
  return screens;
}

/**
 * Map every timed cookpit task id to its wall-clock start (epoch ms) under the
 * same anchor Preview uses — serve time when set, else "now". Lets surfaces
 * that key off task ids (e.g. the Hotspots screen) show times that match the
 * Preview/Planner read-out exactly, with no /anchor call. Derived prerequisite
 * rows are excluded — they aren't cookpit tasks and carry no clock.
 */
export function buildTaskStartMap(
  recipe: Recipe,
  serveTime: ServeTime | null,
  nowMs: number,
): Map<string, number> {
  const cp = recipe.cookpit;
  const wc = computeWholeCookPlan(cp, resolveServeMs(recipe, serveTime, nowMs));
  const startByPhase = new Map(wc.phases.map((p) => [p.phase, p.startMs]));
  const map = new Map<string, number>();
  for (const phaseId of ['preCook', 'prepCook', 'liveCook'] as const) {
    const phase = cp[phaseId];
    const phaseStartMs = startByPhase.get(phaseId);
    if (!phase || phaseStartMs === undefined) continue;
    const cueId = startCueId(phase.tasks);
    for (const task of phase.tasks) {
      if (task.id === cueId) continue;
      map.set(task.id, phaseStartMs + parseTaskTimeSeconds(task.time).seconds * 1000);
    }
  }
  return map;
}
